import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import SpineItem from './SpineItem';

/**
 * Visual approach — 3D cabinet projection shelf:
 *   - Shelf rendered with oblique projection (depth axis at 45° with 50% foreshortening)
 *   - Items have matching skewY(-2deg) transform to align with shelf geometry
 *   - Items positioned to sit ON TOP of the shelf plank surface
 *   - All item types render with transparent backgrounds (no white boxes or borders)
 */

// ─── FormatItem (music) ──────────────────────────────────────────────────────
const FormatItem = ({ item, onClick }) => {
  const format = (item.format || 'CD').toLowerCase();
  const title  = item.name || item.title || 'Unknown';
  const artist = item.artist || '';
  const rawImg = item.imageUrl || item.coverUrl || '';
  const img    = item.processedImageUrl || rawImg;

  const isVinyl    = format.includes('vinyl') || format.includes('lp');
  const isCassette = format.includes('cassette');
  const isBoxSet   = format.includes('box set') || format.includes('box');

  const w = isVinyl ? 120 : isCassette ? 18 : isBoxSet ? 100 : 72;
  const h = isVinyl ? 120 : isCassette ? 128 : isBoxSet ? 130 : 128;

  return (
    <Tooltip title={`${title}${artist ? ` — ${artist}` : ''}`} placement="top" arrow enterDelay={400}>
      <Box onClick={() => onClick?.(item)} sx={{
        position: 'relative',
        width: `${w}px`, height: `${h}px`, flexShrink: 0, cursor: 'pointer',
        transition: 'transform 0.2s ease, filter 0.2s ease',
        transformOrigin: 'bottom center',
        filter: 'drop-shadow(2px 3px 5px rgba(0,0,0,0.6)) drop-shadow(3px 1px 3px rgba(0,0,0,0.3))',
        '&:hover': { transform: 'translateY(-6px)', filter: 'drop-shadow(3px 8px 7px rgba(0,0,0,0.75)) drop-shadow(4px 2px 4px rgba(0,0,0,0.4))', zIndex: 100 },
      }}>
        {isVinyl ? (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <Box sx={{
              position: 'absolute', inset: 0, borderRadius: '3px', overflow: 'hidden',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
              background: img ? `url(${img}) center/cover no-repeat` : '#1a1a1a',
            }}>
              {!img && (
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
                  <Box sx={{ width:'60%', height:'60%', borderRadius:'50%', background:'#111', border:'6px solid #333', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <Box sx={{ width:'20%', height:'20%', borderRadius:'50%', background:'#555' }} />
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{
              position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)',
              width: '90%', height: '90%', borderRadius: '50%',
              background: 'radial-gradient(circle at 50% 50%, #555 15%, #111 15%, #1a1a1a 48%, #222 48%, #1a1a1a 100%)',
              zIndex: -1,
            }} />
          </Box>
        ) : isCassette ? (
          /* Cassette spine — thin upright spine showing edge label */
          <Box sx={{
            width: '100%', height: '100%',
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: '#1a1a2e',
            backgroundImage: img ? `url(${img})` : 'none',
            backgroundSize: 'auto 100%',
            backgroundPosition: 'right center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '1px',
          }}>
            {/* Readability overlay */}
            <Box sx={{
              position: 'absolute', inset: 0,
              background: img
                ? 'linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.1) 100%)'
                : 'linear-gradient(180deg, #1e3a5f 0%, #2d4a7a 100%)',
            }} />
            {/* Left binding shadow */}
            <Box sx={{
              position: 'absolute', top: 0, left: 0, width: '3px', height: '100%',
              background: 'linear-gradient(to right, rgba(0,0,0,0.7), transparent)',
              pointerEvents: 'none',
            }} />
            {/* Vertical title */}
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '8px 2px',
            }}>
              <Box component="span" sx={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                color: '#fff',
                fontSize: '8px',
                fontWeight: 700,
                fontFamily: '"Roboto Condensed", "Arial Narrow", Roboto, Arial, sans-serif',
                letterSpacing: '0.03em',
                lineHeight: 1.1,
                textShadow: '0 1px 4px rgba(0,0,0,1), 0 0 10px rgba(0,0,0,0.9)',
                overflow: 'hidden',
                maxHeight: '100%',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}>
                {title}
              </Box>
            </Box>
            {/* Right edge highlight */}
            <Box sx={{
              position: 'absolute', top: 0, right: 0, width: '1px', height: '100%',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.08) 100%)',
              pointerEvents: 'none',
            }} />
          </Box>
        ) : isBoxSet ? (
          <Box sx={{
            width: '100%', height: '100%',
            background: img ? `url(${img}) center/cover no-repeat` : '#2c1a54',
            borderRadius: '3px', overflow: 'hidden', position: 'relative',
          }}>
            <Box sx={{ position:'absolute', top:0, left:0, width:'12px', height:'100%', background:'rgba(0,0,0,0.4)', borderRight:'1px solid rgba(255,255,255,0.1)' }} />
          </Box>
        ) : (
          /* CD jewel case */
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <Box sx={{ position: 'absolute', inset: 0, background: img ? 'transparent' : '#1a1a2e', borderRadius: '2px', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.15)', overflow: 'hidden' }}>
              {img && <Box component="img" src={img} alt={title} sx={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />}
              {!img && <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', height:'100%', p:'6px', background:'linear-gradient(180deg,#1a1a2e,#2d1b69)' }}>
                <Typography sx={{ color:'#fff', fontSize:'8px', fontWeight:700, textAlign:'center' }}>{title.substring(0, 24)}</Typography>
              </Box>}
            </Box>
            <Box sx={{ position:'absolute', top:'4px', left:'4px', bottom:'4px', width:'2px', background:'rgba(255,255,255,0.2)', borderRadius:'1px' }} />
            <Box sx={{ position:'absolute', top:0, left:0, right:0, height:'40%', background:'linear-gradient(180deg,rgba(255,255,255,0.1) 0%,transparent 100%)', pointerEvents:'none' }} />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

// ─── LegoItem ─────────────────────────────────────────────────────────────────
// Shows the built LEGO set image directly on the shelf — no box border.
const LegoItem = ({ item, onClick }) => {
  const title  = item.name || item.title || 'Unknown Set';
  const setNum = item.setNum || item.set_num || '';
  const rawImg = item.imageUrl || item.set_img_url || item.image_url || item.coverUrl || '';
  const img    = item.processedImageUrl || rawImg;

  const [imgFailed, setImgFailed] = useState(false);
  const [useRaw, setUseRaw] = useState(false);

  // Two-stage fallback:
  //   1. processedImageUrl (transparent-background PNG from rembg)
  //   2. rawImg (original Rebrickable URL) — used if processed image is blank or broken
  //   3. placeholder — used if both sources fail
  const effectiveImg = (imgFailed || useRaw) ? rawImg : img;

  return (
    <Tooltip title={`${title}${setNum ? ` (${setNum})` : ''}`} placement="top" arrow enterDelay={400}>
      <Box onClick={() => onClick?.(item)} sx={{
        position: 'relative',
        width: '110px', height: '95px',
        flexShrink: 0, cursor: 'pointer',
        transition: 'transform 0.2s ease, filter 0.2s ease',
        transformOrigin: 'bottom center',
        filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.95)) drop-shadow(0px 10px 18px rgba(0,0,0,0.7))',
        '&:hover': { transform: 'translateY(-5px)', filter: 'drop-shadow(0px 8px 12px rgba(0,0,0,1)) drop-shadow(0px 18px 28px rgba(0,0,0,0.85))', zIndex: 100 },
      }}>
        {effectiveImg && !imgFailed ? (
          <Box
            component="img"
            src={effectiveImg}
            alt={title}
            onLoad={(e) => {
              // If processed image loaded but is tiny (blank rembg output), fall back to raw
              if (!useRaw && item.processedImageUrl && e.target.naturalWidth < 10) {
                setUseRaw(true);
              }
            }}
            onError={() => {
              if (!useRaw && item.processedImageUrl) {
                setUseRaw(true); // processed URL broken — try raw image
              } else {
                setImgFailed(true); // both sources failed — show placeholder
              }
            }}
            sx={{
              width: '100%', height: '100%',
              objectFit: 'contain',
              objectPosition: 'bottom center',
              display: 'block',
            }}
          />
        ) : (
          <Box sx={{
            width: '100%', height: '100%',
            background: 'linear-gradient(135deg, #c4302022 0%, #1a1a1a 100%)',
            border: '1px solid #c4302044',
            borderRadius: '4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ color: '#aaa', fontSize: '8px', fontWeight: 700, textAlign: 'center', px: '6px', lineHeight: 1.3 }}>
              {title.substring(0, 28)}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

// ─── ConsoleItem ──────────────────────────────────────────────────────────────
// Shows the console hardware photo directly on the shelf — no bounding box.
// Image priority: processedImageUrl (transparent PNG) → imageUrl (stored URL) → platformLogo (brand logo)
const ConsoleItem = ({ item, onClick }) => {
  const title = item.name || item.title || 'Unknown Console';
  const [processedFailed, setProcessedFailed] = useState(false);
  const [rawFailed, setRawFailed] = useState(false);

  useEffect(() => {
    setProcessedFailed(false);
    setRawFailed(false);
  }, [item._id]);

  const rawImg = item.imageUrl || item.platformLogo || '';
  // Two-stage fallback: processedImageUrl first, fall back to rawImg if processed failed
  const effectiveImg = (item.processedImageUrl && !processedFailed)
    ? item.processedImageUrl
    : (!rawFailed ? rawImg : '');

  const getAccentColor = () => {
    const n = title.toLowerCase();
    if (n.includes('playstation') || n.includes('ps')) return '#0070d1';
    if (n.includes('xbox'))  return '#52b043';
    if (n.includes('nintendo') || n.includes('switch') || n.includes('wii') || n.includes('game boy') || n.includes('gameboy') || n.includes('ds')) return '#e60012';
    if (n.includes('sega') || n.includes('mega drive') || n.includes('genesis') || n.includes('dreamcast') || n.includes('saturn')) return '#17488d';
    if (n.includes('atari')) return '#d4a017';
    return '#555';
  };

  return (
    <Tooltip title={title} placement="top" arrow enterDelay={400}>
      <Box onClick={() => onClick?.(item)} sx={{
        position: 'relative',
        height: '95px',
        width: 'auto',
        minWidth: '60px',
        maxWidth: '160px',
        flexShrink: 0, cursor: 'pointer',
        transition: 'transform 0.2s ease, filter 0.2s ease',
        transformOrigin: 'bottom center',
        filter: 'drop-shadow(0px 4px 5px rgba(0,0,0,0.98)) drop-shadow(0px 10px 16px rgba(0,0,0,0.75))',
        '&:hover': {
          transform: 'translateY(-5px)',
          filter: 'drop-shadow(0px 8px 10px rgba(0,0,0,1)) drop-shadow(0px 20px 28px rgba(0,0,0,0.85))',
          zIndex: 100,
        },
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}>
        {effectiveImg ? (
          <Box
            component="img"
            src={effectiveImg}
            alt={title}
            onError={() => {
              if (item.processedImageUrl && !processedFailed) {
                setProcessedFailed(true); // processed URL broke — retry with rawImg
              } else {
                setRawFailed(true); // rawImg also broke — show placeholder
              }
            }}
            sx={{
              width: 'auto',
              height: '95%',
              maxWidth: '160px',
              objectFit: 'contain',
              objectPosition: 'bottom center',
              display: 'block',
            }}
          />
        ) : (
          // Both image sources failed — branded fallback tile
          <Box sx={{
            width: '100px', height: '65%',
            minWidth: '80px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '4px',
            background: `linear-gradient(135deg, ${getAccentColor()}22 0%, #1a1a1a 100%)`,
            border: `1px solid ${getAccentColor()}44`,
          }}>
            <Typography sx={{
              color: '#aaa', fontSize: '9px', fontWeight: 600,
              textAlign: 'center', px: 1, lineHeight: 1.3,
            }}>
              {title}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

// ─── BoardGameItem ────────────────────────────────────────────────────────────
// Board game boxes are chunkier than game cases — wider spine, square-ish art.
// Same 3-face 3D technique as SpineItem but sized for tabletop box proportions.
const BG_TOP_H  = 20;
const BG_SIDE_W = 12;

const BoardGameItem = ({ item, onClick }) => {
  const title    = item.name || 'Unknown';
  const imageUrl = item.imageUrl || '';

  // Width scales with title length like SpineItem, but wider floor/ceiling
  const spineWidth = Math.min(60, Math.max(30, 30 + (title.length * 0.5)));

  const getCategoryColor = () => {
    const c = (item.category || '').toLowerCase();
    if (c.includes('strategy'))  return '#1a3a6e';
    if (c.includes('family'))    return '#4a7c2f';
    if (c.includes('party'))     return '#7a2060';
    if (c.includes('war'))       return '#5a1a0a';
    if (c.includes('fantasy') || c.includes('adventure')) return '#3a1a6e';
    if (c.includes('economic') || c.includes('trading'))  return '#4a3a10';
    if (c.includes('horror') || c.includes('thrill'))     return '#2a0a0a';
    if (c.includes('science') || c.includes('sci-fi'))    return '#0a2a4a';
    if (c.includes('abstract'))  return '#1a1a3a';
    if (c.includes('trivia'))    return '#2a4a1a';
    return '#2e1e10';
  };

  const getCategoryAccent = () => {
    const c = (item.category || '').toLowerCase();
    if (c.includes('strategy'))  return '#4a7abf';
    if (c.includes('family'))    return '#7abf4a';
    if (c.includes('party'))     return '#bf4a9a';
    if (c.includes('war'))       return '#bf6a3a';
    if (c.includes('fantasy') || c.includes('adventure')) return '#7a4abf';
    if (c.includes('economic') || c.includes('trading'))  return '#bfa040';
    if (c.includes('horror') || c.includes('thrill'))     return '#bf3a3a';
    if (c.includes('science') || c.includes('sci-fi'))    return '#3abfbf';
    if (c.includes('abstract'))  return '#9a9abf';
    if (c.includes('trivia'))    return '#6abf6a';
    return '#a07850';
  };

  const baseColor   = getCategoryColor();
  const accentColor = getCategoryAccent();

  return (
    <Tooltip title={title} placement="top" arrow enterDelay={400}>
      <Box
        onClick={() => onClick?.(item)}
        sx={{
          width:  `${spineWidth + BG_SIDE_W}px`,
          height: `${200 + BG_TOP_H}px`,
          position: 'relative',
          flexShrink: 0,
          userSelect: 'none',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, filter 0.2s ease',
          filter: [
            'drop-shadow(0px 3px 5px rgba(0,0,0,0.98))',
            'drop-shadow(0px 16px 24px rgba(0,0,0,0.85))',
          ].join(' '),
          '&:hover': {
            transform: 'translateY(-14px)',
            filter: [
              'drop-shadow(0px 5px 8px rgba(0,0,0,1))',
              'drop-shadow(0px 28px 28px rgba(0,0,0,0.92))',
            ].join(' '),
            zIndex: 50,
          },
        }}
      >
        {/* TOP FACE */}
        <Box sx={{
          position: 'absolute',
          top: 0, left: 0,
          width: `${spineWidth}px`,
          height: `${BG_TOP_H}px`,
          background: 'linear-gradient(to right, rgba(200,140,60,0.6) 0%, rgba(60,40,20,0.9) 40%, rgba(20,14,8,0.95) 100%)',
          backgroundColor: '#1a1208',
          borderRadius: '1px 1px 0 0',
          borderTop: '1px solid rgba(210,160,70,0.3)',
        }} />

        {/* RIGHT SIDE FACE */}
        <Box sx={{
          position: 'absolute',
          top: `${BG_TOP_H}px`,
          left: `${spineWidth}px`,
          width: `${BG_SIDE_W}px`,
          height: '200px',
          overflow: 'hidden',
          backgroundColor: baseColor,
        }}>
          {imageUrl && (
            <Box component="img" src={imageUrl} alt=""
              sx={{ position: 'absolute', top: 0, left: 0, width: 'auto', height: '100%', objectFit: 'cover', objectPosition: 'left center', display: 'block', pointerEvents: 'none' }}
            />
          )}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 100%)',
          }} />
        </Box>

        {/* FRONT FACE */}
        <Box sx={{
          position: 'absolute',
          top: `${BG_TOP_H}px`,
          left: 0,
          width: `${spineWidth}px`,
          height: '200px',
          overflow: 'hidden',
          backgroundColor: baseColor,
          borderRadius: '0 0 1px 0',
        }}>
          {imageUrl && (
            <Box component="img" src={imageUrl} alt={title}
              sx={{ position: 'absolute', top: 0, right: 0, width: 'auto', height: '100%', objectFit: 'cover', objectPosition: 'right center', display: 'block', pointerEvents: 'none' }}
            />
          )}
          {/* Readability overlay */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: imageUrl
              ? 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.08) 100%)'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.08))',
          }} />

          {/* Left binding shadow */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, width: '5px', height: '100%',
            background: 'linear-gradient(to right, rgba(0,0,0,0.7), transparent)',
            pointerEvents: 'none',
          }} />

          {/* Vertical title */}
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '8px 4px 12px 6px',
          }}>
            <Box component="span" sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              fontFamily: '"Roboto Condensed", "Arial Narrow", Roboto, Arial, sans-serif',
              letterSpacing: '0.03em',
              lineHeight: 1.1,
              textShadow: '0 1px 4px rgba(0,0,0,1), 0 0 10px rgba(0,0,0,0.9)',
              overflow: 'hidden',
              maxHeight: '100%',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}>
              {title}
            </Box>
          </Box>

          {/* Category accent bar at bottom */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '4px', backgroundColor: accentColor, opacity: 0.9,
          }} />

          {/* Right edge specular highlight */}
          <Box sx={{
            position: 'absolute', top: 0, right: 0, width: '1px', height: '100%',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
            pointerEvents: 'none',
          }} />
        </Box>
      </Box>
    </Tooltip>
  );
};

// ─── BookShelf ────────────────────────────────────────────────────────────────
/**
 * BookShelf — one row of a virtual shelf drawn in cabinet projection (oblique 3D).
 *
 * Cabinet projection: the depth axis goes up-right at 45° with 50% foreshortening.
 *   D  = depth in px (how deep the shelf appears to recede)
 *   SL = D/2 = vertical component (foreshortening)
 *
 * Three visible faces per shelf:
 *   FRONT — dark back wall where items stand
 *   RIGHT — parallelogram side panel showing the shelf's depth
 *   TOP   — parallelogram top surface of the shelf plank
 */
const D = 30;       // depth in px
const SL = 15;      // D/2 — vertical foreshortening
const PL_H = 18;    // height of shelf plank front face (px)

const getBackH = (type) => {
  if (type === 'lego')     return 120;
  if (type === 'consoles') return 120;
  if (type === 'music')    return 150;
  return 215; // games, movies, default
};

const BookShelf = ({ label, items, onItemClick, color = '#8B4513', collectionType = 'games' }) => {
  const BH = getBackH(collectionType);
  const itemGap = collectionType === 'music' ? '6px'
    : collectionType === 'lego' ? '8px'
    : collectionType === 'consoles' ? '10px'
    : '3px';

  const renderItem = (item, index) => {
    if (collectionType === 'music')       return <FormatItem      key={item._id || index} item={item} onClick={onItemClick} />;
    if (collectionType === 'lego')        return <LegoItem        key={item._id || index} item={item} onClick={onItemClick} />;
    if (collectionType === 'consoles')    return <ConsoleItem     key={item._id || index} item={item} onClick={onItemClick} />;
    if (collectionType === 'board-games') return <BoardGameItem   key={item._id || index} item={item} onClick={onItemClick} />;
    return <SpineItem key={item._id || index} item={item} onClick={onItemClick} lazy={index > 30} />;
  };

  return (
    <Box sx={{ mb: '70px', position: 'relative' }}>
      {/* Label */}
      <Box sx={{ display:'flex', alignItems:'center', mb:'8px', gap:1 }}>
        <Box sx={{
          px: 1.5, py: 0.25, borderRadius: '3px',
          background: color, color: '#fff',
          fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
          boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
        }}>
          {label}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </Typography>
      </Box>

      {/* 3D shelf unit */}
      <Box sx={{ position: 'relative', width: `calc(100% - ${D}px)`, mt: `${SL}px`, height: `${BH + PL_H}px` }}>

        {/* ── BACK WALL (front face) ──────────────────────────────────── */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: `${BH}px`, zIndex: 15 }}>

          {/* Wall surface — wood-plank texture */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: '-12px',
            bottom: 0,
            zIndex: 1,
            transform: `translate(12px, -${PL_H}px)`,
            backgroundImage: 'url(/ShelfBackground.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.55)',
            borderTop: '1px solid rgba(200,136,74,0.18)',
          }} />

          {/* Items row */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: `${BH}px`,
            zIndex: 100,
            display: 'flex',
            gap: itemGap,
            alignItems: 'flex-end',
            overflowX: 'auto',
            overflowY: 'visible',
            pb: '0px',
            pt: '10px',
            px: '8px',
            '&::-webkit-scrollbar': { height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(139,69,19,0.6)', borderRadius: '3px' },
          }}>
            {items.map(renderItem)}
          </Box>

          {/* RIGHT SIDE of back wall — parallelogram */}
          <Box sx={{
            position: 'absolute',
            top: `-${SL}px`, left: '100%',
            width: `${D}px`, height: `${BH + SL}px`,
            clipPath: `polygon(0px ${SL}px, ${D}px 0px, ${D}px ${BH}px, 0px ${BH + SL}px)`,
            backgroundImage: 'url(/ShelfBackground.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'right center',
            filter: 'brightness(0.3)',
            zIndex: 2,
          }} />
        </Box>

        {/* ── SHELF PLANK ─────────────────────────────────────────── */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${PL_H}px`, zIndex: 5 }}>

          {/* TOP FACE of plank — parallelogram */}
          <Box sx={{
            position: 'absolute',
            top: `-${SL}px`, left: 0,
            width: `calc(100% + ${D}px)`, height: `${SL}px`,
            clipPath: `polygon(${D}px 0%, 100% 0%, calc(100% - ${D}px) 100%, 0% 100%)`,
            backgroundImage: 'url(/ShelfBackground.png)',
            backgroundSize: 'auto 100%',
            backgroundRepeat: 'repeat-x',
            filter: 'brightness(0.75)',
            zIndex: 10,
          }} />

          {/* FRONT FACE of plank */}
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: 'url(/ShelfBackground.png)',
            backgroundSize: 'auto 100%',
            backgroundRepeat: 'repeat-x',
            boxShadow: '0 6px 20px rgba(0,0,0,0.75)',
            zIndex: 3,
          }} />

          {/* RIGHT SIDE FACE of plank — parallelogram */}
          <Box sx={{
            position: 'absolute',
            top: `-${SL}px`, left: '100%',
            width: `${D}px`, height: `${SL + PL_H}px`,
            clipPath: `polygon(0px ${SL}px, ${D}px 0px, ${D}px ${PL_H}px, 0px ${SL + PL_H}px)`,
            backgroundImage: 'url(/ShelfBackground.png)',
            backgroundSize: 'auto 100%',
            backgroundRepeat: 'repeat-x',
            filter: 'brightness(0.45)',
            zIndex: 4,
          }} />

          {/* Cast shadow under plank */}
          <Box sx={{
            position: 'absolute',
            top: `${PL_H}px`, left: 0, right: 0,
            height: '18px',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%)',
            pointerEvents: 'none',
          }} />
        </Box>
      </Box>
    </Box>
  );
};

export default BookShelf;
